<template>
  <el-form
    ref="ruleFormRef"
    :model="form"
    :rules="rules"
  >
    <el-form-item>
      <custom-data
        :data="form.Codigo"
        :spanColumn="12"
        label="Código"
      />
    </el-form-item>
    <el-form-item prop="Descripcion">
      <custom-input
        ref="descripcionInputRef"
        v-model="form.Descripcion"
        :spanColumn="24"
        label="Descripción"
        :settings="{
          placeholder: 'Descripción',
          disabled: disabled,
        }"
      />
    </el-form-item>
    <el-form-item>
      <additional-settings-buttons
        :disabled="disabled"
        @add="handleClickAdd"
        @save="handleClickSave"
        @cancel="handleClickCancel"
      />
    </el-form-item>
  </el-form>
  <additional-settings-table
    :data="depositos"
    :columns="columns"
    :requestApi="depositoStore.findAllDeposito"
    @edit="handleClickEdit"
    @delete="handleClickDelete"
  />
</template>

<script setup>
import {
  onMounted,
  ref,
  reactive,
  nextTick,
} from 'vue';
import { storeToRefs } from 'pinia';
import { ElMessageBox } from 'element-plus';
import AdditionalSettingsButtons from './additional_settings_buttons.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomData from '@/shared/components/custom_data.vue';
import { useDepositoStore } from '@/stores/deposito/deposito_store';
import { useLoading } from '@/shared/composables/loading.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import AdditionalSettingsTable from './additional_settings_table.vue';
import deposito_model from '@/shared/models/deposito_model.js';

const depositoStore = useDepositoStore();
const { depositos, deposito } = storeToRefs(depositoStore);

const columns = [
  { field: 'Codigo', label: 'ID', width: 75, sortable: true },
  { field: 'Descripcion', label: 'Descripción', width: 280, sortable: true },
];

const ruleFormRef = ref(null);
const descripcionInputRef = ref(null);

const form = reactive({ ...deposito_model });

const rules = reactive({
  Descripcion: [{ required: true, message: 'Ingrese la descripción', trigger: 'blur' }],
});

const disabled = ref(true);

const handleClickAdd = () => {
  select();
  depositoStore.cleanDeposito();
};

const handleClickEdit = (row) => {
  if (row.Codigo) {
    select(row);
  }
};

const select = (item) => {
  if (item) {
    Object.assign(form, item);
  } else {
    Object.assign(form, deposito_model);
  }
  disabled.value = false;
  nextTick(() => {
    descripcionInputRef.value.focus();
  });
};

const handleClickDelete = (row) => {
  if (row.Codigo) {
    ElMessageBox.confirm(`¿Quiere eliminar el depósito ${row.Descripcion}?`, 'Confirmación', {
      confirmButtonText: 'Si',
      cancelButtonText: 'No',
      type: 'warning',
    })
      .then(() => {
        deleteDeposito(row);
      })
      .catch((err) => {
        return;
      });
  }
};

const deleteDeposito = async (row) => {
  try {
    useLoading().openLoading();

    await depositoStore.deleteDepositoHTTP(row);
    clean();
    await depositoStore.firstRequest();
    useMessageSuccess().open(`El depósito se eliminó exitosamente!`);
  } catch (error) {
    useMessageError().open(`Ocurrió un error al eliminar el depósito!`);
  } finally {
    useLoading().closeLoading();
  }
};

const handleClickSave = async () => {
  try {
    if (!ruleFormRef.value) return;

    const valid = await ruleFormRef.value.validate();

    if (!valid) {
      return;
    }
    await save();
  } catch (err) {}
};

const save = async () => {
  try {
    deposito.value = { ...form };

    useLoading().openLoading();

    await depositoStore.saveDeposito();
    clean();
    await depositoStore.firstRequest();
    useMessageSuccess().open(`Los datos se guardaron exitosamente!`);
  } catch (error) {
    useMessageError().open(`Ocurrió un error al guardar los datos!`);
  } finally {
    useLoading().closeLoading();
  }
};

const handleClickCancel = () => {
  clean();
};

const clean = () => {
  disabled.value = true;
  depositoStore.cleanDeposito();
  ruleFormRef.value.clearValidate();
  Object.assign(form, deposito_model);
};

onMounted(async () => {
  await depositoStore.firstRequest();
});
</script>
